import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import {
  buildDefaultDashboardConfigs,
  buildDefaultPersonaConfig,
  type DashboardPersonaConfig,
} from '@components/pages/Settings/Analytics/Dashboard/dashboardSettings.constants'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

/** Persona settings as persisted by the business/data layer (flat, snake_case). */
export interface DashboardPersonaSettingsDto {
  date_range: string
  refresh_interval_minutes: number
  show_goal_progress: boolean
  show_alert_banners: boolean
  widgets: Record<string, boolean>
}

/** UI-facing map keyed by (kebab-case) persona id → nested display config. */
export type DashboardSettingsMap = Record<string, DashboardPersonaConfig>

/**
 * The backend persona enum uses snake_case (`front_desk`) while the front-desk
 * UI constants use kebab-case (`front-desk`). Convert both ways at the boundary.
 */
const toUiPersonaId = (persona: string): string =>
  persona === 'front_desk' ? 'front-desk' : persona
const toApiPersonaId = (persona: string): string =>
  persona === 'front-desk' ? 'front_desk' : persona

const toPersonaConfig = (
  dto: DashboardPersonaSettingsDto,
  fallback: DashboardPersonaConfig,
): DashboardPersonaConfig => ({
  display: {
    dateRange: dto.date_range ?? fallback.display.dateRange,
    refreshInterval:
      dto.refresh_interval_minutes != null
        ? String(dto.refresh_interval_minutes)
        : fallback.display.refreshInterval,
    showGoalProgress:
      dto.show_goal_progress ?? fallback.display.showGoalProgress,
    showAlertBanners:
      dto.show_alert_banners ?? fallback.display.showAlertBanners,
  },
  // Missing widget keys fall back to defaults, so the UI always has a full map.
  widgets: { ...fallback.widgets, ...(dto.widgets ?? {}) },
})

const toPersonaSettingsDto = (
  config: DashboardPersonaConfig,
): DashboardPersonaSettingsDto => ({
  date_range: config.display.dateRange,
  refresh_interval_minutes: Number(config.display.refreshInterval) || 0,
  show_goal_progress: config.display.showGoalProgress,
  show_alert_banners: config.display.showAlertBanners,
  widgets: config.widgets,
})

/** Overlay the backend response onto a complete default map. */
const mergeSettings = (
  settings: Record<string, DashboardPersonaSettingsDto> | undefined,
): DashboardSettingsMap => {
  const configs = buildDefaultDashboardConfigs()
  Object.entries(settings ?? {}).forEach(([persona, dto]) => {
    const uiId = toUiPersonaId(persona)
    configs[uiId] = toPersonaConfig(
      dto,
      configs[uiId] ?? buildDefaultPersonaConfig(),
    )
  })
  return configs
}

export const getDashboardSettings = ({
  clinicId,
}: {
  clinicId: string
}): Promise<DashboardSettingsMap> =>
  axios
    .get<
      ApiResponse<{ settings: Record<string, DashboardPersonaSettingsDto> }>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/settings`, {
      params: { clinicId },
    })
    .then((response) => mergeSettings(response.data.data.settings))

export const saveDashboardSettings = ({
  clinicId,
  settings,
}: {
  clinicId: string
  settings: DashboardSettingsMap
}): Promise<DashboardSettingsMap> => {
  const payload: Record<string, DashboardPersonaSettingsDto> = {}
  Object.entries(settings).forEach(([persona, config]) => {
    payload[toApiPersonaId(persona)] = toPersonaSettingsDto(config)
  })

  return axios
    .put<
      ApiResponse<{ settings: Record<string, DashboardPersonaSettingsDto> }>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/settings`, {
      clinic_id: clinicId,
      settings: payload,
    })
    .then((response) => mergeSettings(response.data.data.settings))
}
